import { HeroUIProvider } from "@heroui/react";
import { CssBaseline, ThemeProvider as MaterialUIThemeProvider } from "@material-ui/core";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { Suspense, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { Outlet, useNavigate } from "react-router";
import { useEventListener } from "usehooks-ts";

import LoadingScreen from "@/components/LoadingScreen";
import RTL from "@/components/RTL";
import { AuthContextProvider, AuthProvider, useBrandColorContext } from "@/contexts";
import { DEFAULT_PRIMARY_COLOR } from "@/contexts/BrandColorContext";
import { useLocalStorage } from "@/hooks/common/useLocalStorage";
import { usePageViewTracking } from "@/hooks/common/usePageViewTracking";
import useScrollReset from "@/hooks/useScrollReset";
import useSettings from "@/hooks/useSettings";
import { createCustomTheme } from "@/theme";
import { CustomThemeProvider, useTheme } from "@/ui";

const isDevOrStaging = () => {
  const origin = window.location.origin;
  return (
    origin.includes("dashboard-beta.tournated.com") ||
    origin.includes("staging-dashboard.tournated.com") ||
    origin.includes("dev-dashboard.tournated.com") ||
    origin.includes("vercel.app") ||
    origin.includes("localhost")
  );
};

export const Providers = () => {
  return (
    <CustomThemeProvider>
      <ProvidersInternal />
    </CustomThemeProvider>
  );
};

const ProvidersInternal = () => {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { nextTheme } = useTheme();
  const { brandColor } = useBrandColorContext();
  const [_, setUseNewDesign] = useLocalStorage<boolean>("newDesign", false);
  useScrollReset();
  usePageViewTracking();

  const theme = createCustomTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
    theme: settings.theme,
    primaryColor: brandColor || DEFAULT_PRIMARY_COLOR,
  });

  // Logic to toggle new design with a hotkey press (e.g., Ctrl+Shift+D or Ctrl+Shift+Y / Cmd+Shift+D or Cmd+Shift+Y on Mac)
  useEffect(() => {
    if (!isDevOrStaging()) {
      return;
    }

    const handleHotkey = (e: KeyboardEvent) => {
      const modifier = e.metaKey || e.ctrlKey;
      if (modifier && e.shiftKey && (e.code === "KeyD" || e.code === "KeyY")) {
        e.preventDefault();
        setUseNewDesign((prev: boolean) => {
          const newVal = !prev;
          return newVal;
        });
        window.location.reload();
      }
    };

    window.addEventListener("keydown", handleHotkey);
    return () => window.removeEventListener("keydown", handleHotkey);
  }, [setUseNewDesign]);

  useEventListener("keydown", (e) => {
    if (!isDevOrStaging()) {
      return;
    }

    const modifier = e.metaKey || e.ctrlKey;
    if (modifier && e.shiftKey && e.code === "KeyT") {
      e.preventDefault();
      nextTheme();
    }
  });

  return (
    <NuqsAdapter>
      <AuthProvider>
        <AuthContextProvider>
          <MaterialUIThemeProvider theme={theme}>
            <HeroUIProvider className="h-full w-full" navigate={navigate}>
              <RTL direction={settings.direction ?? "ltr"}>
                <CssBaseline />
                <Toaster position="top-center" />
                <Suspense fallback={<LoadingScreen />}>
                  <Outlet />
                </Suspense>
              </RTL>
            </HeroUIProvider>
          </MaterialUIThemeProvider>
        </AuthContextProvider>
      </AuthProvider>
    </NuqsAdapter>
  );
};
